/* ----------------------------------------------------------------------------
   SN theme customizations
   Loaded after theme.js in layout/theme.liquid — keep custom scripts here.
   ---------------------------------------------------------------------------- */

/* Reset the quantity selector to its default value whenever the selected
   variant changes, so a quantity chosen for one variant doesn't carry over
   to the next. Applies to the product page, featured product and quick view
   (each scoped by the product form the event reports). */

document.addEventListener('variant:change', (event) => {
  /* The event is dispatched on the product form itself. Note: the form's
     "id" property can't be trusted here — it resolves to the hidden
     <input name="id"> control, so read the attribute instead. */
  const formId = event.target instanceof HTMLFormElement && event.target.getAttribute('id');

  if (!formId) {
    return;
  }

  document
    .querySelectorAll(`input[name="quantity"][form="${CSS.escape(formId)}"]`)
    .forEach((input) => input.closest('quantity-selector')?.restoreDefaultValue());
});

/* Keep the cart drawer's "Checkout" button disabled until the terms checkbox
   is checked. The checkbox's `required` attribute alone only stops the
   browser's native form submission — third-party scripts (e.g. the Breeze
   SDK) that attach their own click listener directly to the checkout button
   still fire on click regardless of form validity. A `disabled` button never
   dispatches a click event at all, so those listeners never see the click
   until terms are accepted. */

function syncCheckoutButtonState(root) {
  root.querySelectorAll('input[name="attributes[Terms and conditions accepted]"]').forEach((checkbox) => {
    const checkoutButton = checkbox.closest('form')?.querySelector('button[name="checkout"]');

    if (checkoutButton) {
      checkoutButton.disabled = !checkbox.checked;
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => syncCheckoutButtonState(document));
} else {
  syncCheckoutButtonState(document);
}

document.addEventListener('cart-drawer:refreshed', () => syncCheckoutButtonState(document));

document.addEventListener('change', (event) => {
  if (event.target.matches?.('input[name="attributes[Terms and conditions accepted]"]')) {
    syncCheckoutButtonState(event.target.closest('form') ?? document);
  }
});

/* Empty the cart once a signed-in customer signs out. Sign-out happens on
   Shopify's hosted new-customer-accounts UI (shopify.com/<id>/account), a
   separate origin the theme has no hook into, so this can't be tied to the
   "Sign out" click itself. Instead, compare the customer id on every
   storefront page load against the last id we saw in localStorage: if we
   previously saw a signed-in customer and now see none, the cart is stale
   from that session and gets cleared. */

const SIGNED_OUT_CART_STORAGE_KEY = 'sn:lastCustomerId';

function clearCartOnSignOut() {
  const customerId = window.themeVariables?.customer?.id;
  const lastCustomerId = window.localStorage.getItem(SIGNED_OUT_CART_STORAGE_KEY);

  if (customerId) {
    window.localStorage.setItem(SIGNED_OUT_CART_STORAGE_KEY, String(customerId));
    return;
  }

  if (!lastCustomerId) {
    return;
  }

  window.localStorage.removeItem(SIGNED_OUT_CART_STORAGE_KEY);

  fetch('/cart/clear.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' }
  })
    .then(() => document.dispatchEvent(new CustomEvent('cart:refresh')))
    .catch(() => {});
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', clearCartOnSignOut);
} else {
  clearCartOnSignOut();
}

/* Close the Wishlist Hero "My Wishlist" popup before its "Login" link
   navigates to Shopify's account login. Shopify intercepts that link to open
   its own sign-in modal in place, but Wishlist Hero's popup (a MUI dialog
   with a very high z-index) never closes on its own, so it stays stacked on
   top of the sign-in modal underneath it. Capture phase so this runs before
   Shopify's own handler opens the sign-in modal. */

document.addEventListener('click', (event) => {
  const loginLink = event.target.closest('a[href*="/customer_authentication/login"]');
  const dialog = loginLink?.closest('[role="dialog"]');

  if (dialog?.querySelector('.wishlist-hero-header')) {
    dialog.querySelector('.wishlist-hero-header-close')?.click();
  }
}, true);

/* Open the cart drawer after "Add to cart" on the Wishlist Hero page/widget.
   Wishlist Hero posts to the cart with its own AJAX call, bypassing our
   <product-form>, so the theme's "cart:change" event (the only thing
   cart-drawer listens for to auto-open) never fires. Wishlist Hero does
   dispatch "ajaxProduct:added" on a successful add — a generic event name
   several Shopify apps use for exactly this integration point (confirmed by
   reading their bundle: it relays their own EVENTS_CART_UPDATED event to it).
   Bridge it to the drawer's own open+refresh pipeline instead of duplicating
   that logic: calling .show() flips the "open" attribute, which already
   triggers cart-drawer's "dialog:before-show" handler to refetch fresh cart
   content — the same path a normal cart-icon click takes. */

document.addEventListener('ajaxProduct:added', () => {
  if (window.themeVariables?.settings?.cartType === 'drawer') {
    document.querySelector('cart-drawer')?.show();
  }
});

/* Close the nav drawer before the signed-out-avatar link inside it opens the
   Breeze login popup. Mirrors the Wishlist Hero fix above: the drawer (a
   header-sidebar dialog, z-index: 999) never closes on its own when Breeze
   mounts its popup, so without this the popup — even forced above the drawer
   via z-index in custom.css — sits on top of a nav panel the customer no
   longer needs to see. Capture phase so this runs before Breeze's own
   listener opens the popup.

   Signed-in customers must NOT hit this: their account menu isn't a separate
   floating popup like Breeze's — shopify-account renders it inline, inside
   this same drawer's footer, so closing the drawer on that click closes the
   menu with it before the customer can see it. */

document.addEventListener('click', (event) => {
  const drawer = event.target.closest('.header-sidebar');

  if (drawer && !window.themeVariables?.customer?.id && event.target.closest('shopify-account')) {
    drawer.hide?.();
  }
}, true);
